/**
 * Link detection utilities for team member social links
 */

import { BARE_EMAIL_PATTERN, detectDomainSocialPlatform, normalizeUrl } from "@/lib/utils/social-platform-links";
import type { FooterSocialLinkType, TeamMemberLinkType } from "../_types";

// Re-exported so existing imports of `normalizeUrl` from this module keep working — the
// implementation now lives in the shared `social-platform-links` util. `toVideoEmbedUrl` below also
// uses it locally, hence the plain `import` above rather than a re-export-only statement.
export { normalizeUrl };

/**
 * Detects the social platform from a URL string.
 * Automatically normalizes URLs without a protocol (e.g. "linkedin.com" → "https://linkedin.com").
 * @param url - The URL to analyze
 * @returns The detected link type, or "other" if unrecognized
 */
export function detectLinkType(url: string): TeamMemberLinkType {
  if (!url.trim()) return "other";
  return detectDomainSocialPlatform(url) ?? "other";
}

/** Allowlisted video hosts. Embeds are only built for these; anything else is rejected. */
const YOUTUBE_HOSTS = ["youtube.com", "www.youtube.com", "m.youtube.com", "youtu.be"];
const VIMEO_HOSTS = ["vimeo.com", "www.vimeo.com", "player.vimeo.com"];

/** Bare YouTube/Vimeo video id (alphanumeric, dash, underscore). */
const VIDEO_ID_PATTERN = /^[a-zA-Z0-9_-]+$/;

/**
 * Convert a user-supplied YouTube/Vimeo URL into a safe `<iframe>` embed URL.
 *
 * Only allowlisted hosts (YouTube, Vimeo) produce a result — any other host, a malformed
 * URL, or a missing video id returns `null` so the caller never injects an arbitrary URL
 * into an iframe `src`. Accepts watch, short-link, shorts, and already-embed forms.
 *
 * @param url - The raw URL pasted by the organizer (protocol optional).
 * @returns An `https://…/embed/<id>` URL for a known host, or `null` if unsupported/invalid.
 */
export function toVideoEmbedUrl(url: string): string | null {
  const trimmed = url.trim();
  if (!trimmed) return null;

  let parsed: URL;
  try {
    parsed = new URL(normalizeUrl(trimmed));
  } catch {
    return null;
  }
  // HTTPS only (normalizeUrl already upgrades protocol-less input to https).
  if (parsed.protocol !== "https:") return null;

  const host = parsed.hostname.toLowerCase();

  if (YOUTUBE_HOSTS.includes(host)) {
    const id = extractYouTubeId(parsed);
    return id ? `https://www.youtube.com/embed/${id}` : null;
  }

  if (VIMEO_HOSTS.includes(host)) {
    const id = extractVimeoId(parsed);
    return id ? `https://player.vimeo.com/video/${id}` : null;
  }

  return null;
}

/** Pull the 11-ish char video id from any YouTube URL shape, or null. */
function extractYouTubeId(parsed: URL): string | null {
  // youtu.be/<id>
  if (parsed.hostname.toLowerCase() === "youtu.be") {
    const id = parsed.pathname.slice(1).split("/")[0];
    return VIDEO_ID_PATTERN.test(id) ? id : null;
  }
  // youtube.com/watch?v=<id>
  const v = parsed.searchParams.get("v");
  if (v && VIDEO_ID_PATTERN.test(v)) return v;
  // youtube.com/embed/<id> or /shorts/<id> or /v/<id>
  const match = parsed.pathname.match(/^\/(?:embed|shorts|v)\/([a-zA-Z0-9_-]+)/);
  return match ? match[1] : null;
}

/** Pull the numeric video id from a Vimeo URL, or null. */
function extractVimeoId(parsed: URL): string | null {
  // Anchored to the start: vimeo.com/<id> or player.vimeo.com/video/<id>. Channel/album paths
  // like /channels/x/123 intentionally don't match — they aren't a direct video URL.
  const match = parsed.pathname.match(/^\/(?:video\/)?(\d+)/);
  return match ? match[1] : null;
}

/**
 * Detects the footer social link platform from a URL or email string.
 * Extends `detectLinkType` with "email" detection (mailto: URIs or bare emails).
 * @param value - The URL or email to analyze
 * @returns The detected footer social link type, or "other" if unrecognized
 */
export function detectFooterSocialLinkType(value: string): FooterSocialLinkType {
  const trimmed = value.trim();
  if (!trimmed) return "other";
  if (trimmed.toLowerCase().startsWith("mailto:") || BARE_EMAIL_PATTERN.test(trimmed)) return "email";
  return detectLinkType(trimmed);
}
